import '../../styles/app.scss';

import { APP_ROUTES, APP_ROUTE_GROUPS } from '../constants/app-routes';
import { Route, Switch, matchPath, useLocation } from 'react-router-dom';

import AuthenticatedRoutes from './routes/authenticated';
import { LDProvider as LaunchDarklyProvider } from 'launchdarkly-react-client-sdk';
import React from 'react';
import SignInRoutes from './routes/sign-in';
import UnauthenticatedRoutes from './routes/unauthenticated';
import { UserContextProvider } from '../context/user-context';
import { basicLogger as launchDarklyBasicLogger } from 'launchdarkly-js-client-sdk';
import scrollContentIntoView from '../utilities/scroll-content-into-view';
import useAnalyticsIdentify from '../hooks/analytics/use-analytics-identify';
import { useInteractionTracker } from '@blueshift-ui/telemetry';
import useLabUser from '../hooks/data-access/use-lab-user';
import useViewTracking from '@blueshift-ui/analytics/dist/hooks/use-view-tracking';

//

const SCROLL_TO_TOP_ROUTE_EXCEPTIONS = [
  APP_ROUTES.assignedSkillQuestion,
  APP_ROUTES.activityLabsQuizQuestion,
  APP_ROUTES.onDemandVideo,
  APP_ROUTES.skillQuestion,
];

//

function App() {
  const location = useLocation();
  const { hash, pathname, search } = location;
  const labUser = useLabUser();
  const { trackEvent } = useInteractionTracker();

  const trackedProperties = React.useMemo(
    () => ({
      category: 'learning-lab',
      metadata: { location },
    }),
    [location]
  );
  useViewTracking({
    trackedProperties,
    type: 'page',
  });

  useAnalyticsIdentify({ labUser: labUser.activeUser });

  const previousPath = React.useRef();
  const LaunchDarklyProviderProps = {
    clientSideID: process.env.REACT_APP_LD_KEY,
    options: {
      logger: launchDarklyBasicLogger({ level: 'debug' }),
      useReport: true,
    },
    reactOptions: {
      useCamelCaseFlagKeys: false,
    },
  };

  //
  // Side-Effects

  React.useEffect(
    function _handleLocationChange() {
      const path = `${pathname}${search}`;

      if (previousPath.current === path) {
        return;
      }

      if (!hash && !matchPath(pathname, { path: SCROLL_TO_TOP_ROUTE_EXCEPTIONS })) {
        scrollContentIntoView();
      }

      trackEvent({
        action: 'page-view',
        category: 'learning-lab',
        label: `${pathname}${hash}${search}`,
      });

      previousPath.current = path;
    },
    [hash, pathname, search, trackEvent]
  );

  //
  // Render

  return (
    <UserContextProvider {...{ labUser }}>
      <LaunchDarklyProvider {...LaunchDarklyProviderProps}>
        <Switch>
          {/* Routes that don't need LL auth */}
          <Route exact path={APP_ROUTE_GROUPS.unauthenticated}>
            <UnauthenticatedRoutes />
          </Route>

          {/* Login and account creation routes */}
          <Route exact path={APP_ROUTE_GROUPS.signIn}>
            <SignInRoutes />
          </Route>

          {/* Everything else we can assume requires prior LL auth (and if not, kick 'em out) */}
          <Route path={APP_ROUTES.catchAll}>
            <AuthenticatedRoutes />
          </Route>
        </Switch>
      </LaunchDarklyProvider>
    </UserContextProvider>
  );
}

// Exports
// -------

export default App;
